import RegistrationPasswordFields, {passwordIssues} from '../registration/RegistrationPasswordFields';
import {FormEvent, useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, Stack, TextField, Typography} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import RegistrationLayout from '../registration/RegistrationLayout';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {forgotPassword, resetPassword} from '../../services/PasswordRecoveryClient';
import {getApiErrorMessage, getApiFieldErrors, normalizeApiError} from '../../common/apiErrors';

export default function PasswordRecoveryPage({mode, onPasswordReset}: {mode: 'forgot' | 'reset'; onPasswordReset: () => void}) {
    const {t} = useAppTranslation();
    const isReset = mode === 'reset';
    const [credentials, setCredentials] = useState(() => {
        const params = new URLSearchParams(window.location.hash.slice(1));
        return isReset ? {userId: params.get('userId') || '', token: params.get('token') || ''} : {userId: '', token: ''};
    });
    useEffect(() => {
        if (isReset) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }, [isReset]);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [pending, setPending] = useState(false);
    const busy = useRef(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<unknown>();
    const [availableAt, setAvailableAt] = useState(0);
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!availableAt) return;
        const timer = window.setInterval(() => setNow(Date.now()), 250);
        return () => window.clearInterval(timer);
    }, [availableAt]);
    const remaining = Math.max(0, Math.ceil((availableAt - now) / 1000));
    const problem = normalizeApiError(error);
    const invalid = isReset && (!credentials.userId || !credentials.token || problem.code === 'password.reset_invalid');
    const fields = Object.fromEntries(Object.entries(error ? getApiFieldErrors(error, t) : {}).map(([key, value]) => [key.toLowerCase(), value]));
    const emailError = submitted && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? t('auth:email_invalid') : fields.email;
    const policyFailed = passwordIssues(password, true).length > 0;

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (busy.current || remaining || invalid) return;
        setSubmitted(true);
        setError(undefined);
        if (isReset ? policyFailed || !confirmation || confirmation !== password : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return;
        busy.current = true; setPending(true);
        try {
            if (isReset) {
                await resetPassword({...credentials, newPassword: password, confirmPassword: confirmation});
                setCredentials({userId: '', token: ''}); setPassword(''); setConfirmation('');
                onPasswordReset();
            } else {
                await forgotPassword(email.trim());
                const time = Date.now(); setNow(time); setAvailableAt(time + 60000);
            }
            setSuccess(true);
        } catch (failure) {
            setError(failure);
            if (normalizeApiError(failure).status === 429) {
                const seconds = (failure as {retryAfterSeconds?: number}).retryAfterSeconds || 60;
                const time = Date.now(); setNow(time); setAvailableAt(time + seconds * 1000);
            }
        } finally {busy.current = false; setPending(false);}
    };

    return <RegistrationLayout backToLogin={!isReset}><Stack spacing={3} sx={{'& .MuiInputBase-root, & input': {cursor: 'default'}}}>
        <Typography component="h1" variant="h3">{t(isReset ? 'auth:recovery.reset_title' : 'auth:recovery.forgot_title')}</Typography>
        {success && <Alert severity="success">{t(isReset ? 'auth:recovery.changed' : 'auth:recovery.sent')}</Alert>}
        {isReset && success ? <Button component={RouterLink} to="/login" variant="contained">{t('auth:login_btn')}</Button>
            : invalid ? <><Alert severity="error">{t('auth:recovery.invalid')}</Alert><Button component={RouterLink} to="/forgot-password">{t('auth:recovery.request_new')}</Button></>
            : <>
                {!!error && <Alert severity="error">{problem.status === 429 ? t('auth:recovery.cooldown', {seconds: remaining})
                    : problem.code === 'password.delivery_unavailable' ? t('auth:recovery.unavailable')
                    : problem.code === 'password.policy_failed' ? (fields.newpassword || t('auth:recovery.policy')) : getApiErrorMessage(error, t)}</Alert>}
                <Box component="form" noValidate onSubmit={submit} aria-busy={pending}><Stack spacing={2.5}>
                    {isReset ? <RegistrationPasswordFields recovery submitted={submitted} disabled={pending}
                        password={password} confirmPassword={confirmation} passwordError={fields.newpassword} confirmationError={fields.confirmpassword}
                        onPasswordChange={value => {setPassword(value); setError(undefined);}}
                        onConfirmPasswordChange={value => {setConfirmation(value); setError(undefined);}}/>
                    : <TextField label={t('auth:email_holder')} name="email" type="email" autoComplete="email" required fullWidth disabled={pending}
                        value={email} onChange={e => {setEmail(e.target.value); setError(undefined);}} error={!!emailError} helperText={emailError}/>}
                    <Button type="submit" variant="contained" disabled={pending || remaining > 0}>{remaining > 0 ? t('auth:recovery.cooldown', {seconds: remaining}) : t(isReset ? 'auth:recovery.save' : 'auth:recovery.send')}</Button>
                </Stack></Box>
            </>}
    </Stack></RegistrationLayout>;
}
